import { Box, Flex, ThemeIcon, Text, Badge, Tooltip, UnstyledButton, Group } from '@mantine/core';
import { IconCheck, IconX, IconPackage, IconTruckDelivery, IconReceipt, IconRefresh, IconDownload } from '@tabler/icons-react';
import { formatPaymentState, formatCurrency, getPaymentType, truncateTransactionId, formatDate } from '../../utils/paymentUtils';
import { useNavigate } from 'react-router-dom';

interface PaymentHistoryItemProps {
    id: number;
    transactionId: string;
    amount: number;
    currency: string;
    created_at: string;
    packageId?: number;
    combinedPackageId?: number;
    shipmentId?: number;
    description?: string;
    shipmentOrderId?: string;
    state: string;
}

const PaymentHistoryItem = ({
    id,
    transactionId,
    amount,
    currency,
    created_at,
    description,
    shipmentOrderId,
    packageId,
    combinedPackageId,
    state
}: PaymentHistoryItemProps) => {
    const isSuccess = state.toUpperCase() === 'CONFIRMED';
    const isRefunded = state.toUpperCase().includes('REFUND');
    const isPending = state.toUpperCase() === 'PENDING';
    const date = new Date(created_at);
    const navigate = useNavigate();

    // Get appropriate icon based on payment type and state
    const getStateIcon = () => {
        if (isSuccess) return <IconCheck size={20} />;
        if (isRefunded) return <IconRefresh size={20} />;
        if (isPending) return <IconReceipt size={20} />;
        return <IconX size={20} />;
    };

    // Get appropriate icon based on payment type
    const getTypeIcon = () => {
        if (shipmentOrderId) return <IconTruckDelivery size={16} />;
        return <IconPackage size={16} />;
    };

    // Get color based on state
    const getStateColor = () => {
        if (isSuccess) return "teal";
        if (isRefunded) return "blue";
        if (isPending) return "yellow";
        return "red";
    };

    const handleClick = () => {
        navigate(`/payment/${id}`);
    };

    return (
        <UnstyledButton
            w="100%"
            onClick={handleClick}
            style={{
                width: '100%',
                transition: 'background-color 150ms ease',
                borderRadius: '4px'
            }}
            className="payment-history-item"
        >
            <Flex p="md" justify="space-between" gap="md">
                <Flex gap="md" align="center">
                    <ThemeIcon
                        color={getStateColor()}
                        variant="light"
                        size="xl"
                        radius="xl"
                    >
                        {getStateIcon()}
                    </ThemeIcon>

                    <Box>
                        <Flex align="center" gap={5}>
                            {getTypeIcon()}
                            <Text fw={500} size="sm">{getPaymentType({ shipmentOrderId, combinedPackageId, packageId })}</Text>
                        </Flex>
                        <Tooltip label={formatDate(date, 'long')} withArrow position="top">
                            <Text size="xs" color="dimmed">{formatDate(date, 'short')}</Text>
                        </Tooltip>
                        {description && (
                            <Text size="xs" lineClamp={1} maw={200} mt={2}>
                                {description}
                            </Text>
                        )}
                        {!isSuccess && <Badge color={getStateColor()} size="xs" mt={5}>{formatPaymentState(state)}</Badge>}
                    </Box>
                </Flex>

                <Box ta="right">
                    <Group gap="xs">
                        <Text fw={600} size="sm">
                            {isSuccess || isPending ? formatCurrency(amount, currency) : formatCurrency(0, currency)}
                        </Text>
                        {isSuccess && (
                            <Tooltip label="View Invoice">
                                <IconDownload size={16} style={{ marginLeft: '4px' }} />
                            </Tooltip>
                        )}
                    </Group>
                    <Flex gap="xs" justify="flex-end" align="center">
                        <Text size="xs" color="dimmed">
                            ID: {truncateTransactionId(transactionId)}
                        </Text>
                    </Flex>
                    {shipmentOrderId && (
                        <Text size="xs" color="dimmed">Order: {shipmentOrderId}</Text>
                    )}
                </Box>
            </Flex>
        </UnstyledButton>
    );
};

export default PaymentHistoryItem;
